/**
 * Phase 2 MM10 — Visual Concepts tab (project studio).
 * Provider status banner (disabled/mock/live_gated, honest) → concept
 * set list + create form → generate → gallery (auth preview route) →
 * select/reject → export preview pack. No fake image claims.
 */

import { useCallback, useEffect, useState } from "react";
import {
  createVisualConceptSet, exportVisualPreviewPack, generateVisualConceptSet,
  getImageStatus, getVisualAssets, getVisualConceptSets, rejectVisualAsset,
  selectVisualAsset, visualAssetPreviewUrl,
} from "../api/client";
import { Button } from "../components/Button";
import { StatusPill } from "../components/StatusPill";
import type { StudioStrings } from "../i18n/studioStrings";

type Row = Record<string, unknown>;
const OBJECTS = ["burger_box", "pizza_box", "fries_box", "delivery_bag",
  "wrap_paper", "sticker_seal", "cup_sleeve", "jar_label", "bottle_label",
  "pouch", "folding_carton", "rigid_box", "ecommerce_mailer",
  "beauty_packaging", "food_packaging"];

export function VisualConceptsTab({ projectId, t }:
  { projectId: string; t: StudioStrings }): JSX.Element {
  const [provider, setProvider] = useState<Row | null>(null);
  const [sets, setSets] = useState<Row[]>([]);
  const [assets, setAssets] = useState<Row[]>([]);
  const [activeSet, setActiveSet] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({ title: "", packaging_object_type: "burger_box",
    concept_direction: "", style_direction: "", requested_image_count: 3 });

  const refresh = useCallback(async () => {
    const [st, ls] = await Promise.all([
      getImageStatus(), getVisualConceptSets(projectId)]);
    if (st.outcome === "ok") setProvider((st.data?.status ?? null) as Row | null);
    if (ls.outcome === "ok") setSets(ls.data?.items ?? []);
    else if (ls.status === 403) setNotice(t.permissionDenied);
  }, [projectId, t.permissionDenied]);

  const loadGallery = useCallback(async (setId: string) => {
    setActiveSet(setId);
    const r = await getVisualAssets(projectId, setId);
    if (r.outcome === "ok") setAssets(r.data?.items ?? []);
  }, [projectId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const mode = String(provider?.mode ?? "disabled");
  const canGenerate = provider?.generation_available === true;

  async function onCreate(): Promise<void> {
    if (form.title.trim().length < 2) { setNotice(t.loadError); return; }
    const r = await createVisualConceptSet(projectId, form);
    setNotice(r.outcome === "ok" ? null
      : r.status === 403 ? t.permissionDenied : r.errorMessage);
    if (r.outcome === "ok") setForm({ ...form, title: "" });
    await refresh();
  }

  async function onGenerate(setId: string): Promise<void> {
    setBusy(true);
    const r = await generateVisualConceptSet(projectId, setId);
    setBusy(false);
    setNotice(r.outcome === "ok" ? null
      : r.status === 403 ? t.permissionDenied : r.errorMessage);
    await refresh();
    if (r.outcome === "ok") await loadGallery(setId);
  }

  async function onExport(setId: string): Promise<void> {
    const r = await exportVisualPreviewPack(projectId, setId);
    setNotice(r.outcome === "ok"
      ? `${t.exportPack}: ${r.data?.artifact_id ?? "—"}` : r.errorMessage);
  }

  return (
    <section data-testid="project-visuals">
      <p className="po-disclaimer" data-testid="visuals-honesty">
        {t.conceptPreviewOnly}
      </p>
      <p data-testid="visuals-provider">
        <StatusPill tone={canGenerate ? "ok" : "info"} label={mode} />{" "}
        {mode === "disabled" ? t.disabledNotice : null}
        {mode === "mock" ? t.mockNotice : null}
      </p>
      {notice ? <p role="alert" className="po-error">{notice}</p> : null}

      <div className="po-row" data-testid="visuals-create">
        <input placeholder={t.nameLabel} value={form.title}
               data-testid="visuals-title"
               onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <select value={form.packaging_object_type} aria-label={t.objectType}
                onChange={(e) => setForm({ ...form, packaging_object_type: e.target.value })}>
          {OBJECTS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <input placeholder={t.conceptDirection} value={form.concept_direction}
               onChange={(e) => setForm({ ...form, concept_direction: e.target.value })} />
        <input placeholder={t.styleDirection} value={form.style_direction}
               onChange={(e) => setForm({ ...form, style_direction: e.target.value })} />
        <select value={String(form.requested_image_count)} aria-label={t.imageCount}
                onChange={(e) => setForm({ ...form, requested_image_count: Number(e.target.value) })}>
          {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <Button variant="primary" onClick={() => void onCreate()}>{t.newConceptSet}</Button>
      </div>

      {sets.length === 0 ? <p data-testid="visuals-empty">{t.emptyList}</p> : (
        <table className="po-table" data-testid="visuals-sets">
          <thead><tr><th>{t.nameLabel}</th><th>{t.objectType}</th>
            <th>{t.statusLabel}</th><th>{t.costEstimate}</th><th /></tr></thead>
          <tbody>
            {sets.map((s) => (
              <tr key={String(s.id)}>
                <td onClick={() => void loadGallery(String(s.id))}
                    style={{ cursor: "pointer" }}>{String(s.title)}</td>
                <td>{String(s.packaging_object_type)}</td>
                <td><StatusPill tone={s.status === "generated" ? "ok" : "info"}
                                label={String(s.status)} />
                    {s.provider_used ? ` · ${String(s.provider_used)}` : null}</td>
                <td>${String(s.cost_estimate_usd ?? 0)}</td>
                <td>
                  <Button variant="primary" disabled={!canGenerate || busy}
                          onClick={() => void onGenerate(String(s.id))}>
                    {t.generateImages}
                  </Button>{" "}
                  <Button variant="secondary"
                          onClick={() => void onExport(String(s.id))}>
                    {t.exportPack}
                  </Button>
                </td>
              </tr>))}
          </tbody>
        </table>)}

      {activeSet ? (
        <div data-testid="visuals-gallery" className="po-columns">
          {assets.length === 0 ? <p>{t.emptyList}</p> : assets.map((a) => (
            <figure key={String(a.id)} className="po-card">
              <img src={visualAssetPreviewUrl(projectId, String(a.id))}
                   alt={String(a.filename)} style={{ maxWidth: "100%" }} />
              <figcaption>
                <StatusPill tone={a.status === "selected" ? "ok"
                  : a.status === "rejected" ? "warn" : "info"}
                            label={String(a.status)} />
                {a.is_mock ? " · MOCK" : null}
                {" "}{String(a.reviewer_notes ?? "")}
                <div>
                  <Button variant="secondary" onClick={() =>
                    void selectVisualAsset(projectId, String(a.id))
                      .then(() => loadGallery(activeSet))}>
                    {t.selectConcept}
                  </Button>{" "}
                  <Button variant="secondary" onClick={() =>
                    void rejectVisualAsset(projectId, String(a.id))
                      .then(() => loadGallery(activeSet))}>
                    {t.rejectConcept}
                  </Button>
                </div>
              </figcaption>
            </figure>))}
        </div>) : null}
    </section>
  );
}
